// nav/overlay-nav.js: Phase 3 #5b: pure overlay-navigation backbone.
//
// Models a single overlay LAYER that floats above whatever live view is
// already mounted (home lobby / in-room lobby / in-game table). The model
// deliberately encodes ONLY the overlay key and nothing about the screen
// underneath it.
//
// Why that matters (the #1 risk in Phase 3): the room WebSocket lives in
// useRoomWs, whose unmount cleanup CLOSES the socket. If "How to Play"
// were a route that swapped RoomView out, an in-room/in-game player would
// be silently disconnected. By making the overlay a pure additive layer,
// rendered ABOVE RoomView in App.jsx while RoomView stays mounted, opening
// and closing it is a no-op on the live view tree. This module captures
// that invariance: open→close round-trips to the start state and never
// references the underlying screen.
//
// #1 (settings) and #7 (future overlays) reuse this backbone by adding
// more overlay keys; the transitions stay pure and framework-agnostic.

/** @typedef {null | 'how-to-play'} OverlayKey */
/** @typedef {{ overlay: OverlayKey }} NavState */

/**
 * The history.state key we stamp when an overlay is open, so a browser
 * Back / popstate can be recognized as "close the overlay" rather than a
 * real navigation. Namespaced to avoid clashing with any other state.
 */
export const HISTORY_MARKER = 'euchredev_overlay';

/**
 * The starting nav state: no overlay open.
 * @returns {NavState}
 */
export function initialNavState() {
  return { overlay: null };
}

/**
 * Open the "How to Play" overlay. Pure, returns a new state and never
 * touches the underlying screen.
 * @param {NavState} _state
 * @returns {NavState}
 */
export function openHowToPlay(_state) {
  return { overlay: 'how-to-play' };
}

/**
 * Close whatever overlay is open, returning to the underlying screen.
 * Closing is a no-op on the live view (it was never represented here), so
 * the user lands back on exactly the screen they left. Idempotent.
 * @param {NavState} _state
 * @returns {NavState}
 */
export function closeOverlay(_state) {
  return { overlay: null };
}

/**
 * @param {NavState} state
 * @returns {boolean} whether any overlay is currently open.
 */
export function isOverlayOpen(state) {
  return state.overlay !== null;
}

/**
 * Does a history.state object carry our overlay marker? Used by the
 * popstate listener to decide whether a Back press should close the
 * overlay (true) or fall through as a normal navigation (false).
 * @param {unknown} historyState
 * @returns {boolean}
 */
export function isOverlayMarkerState(historyState) {
  return (
    typeof historyState === 'object' &&
    historyState !== null &&
    HISTORY_MARKER in historyState
  );
}
